/**
 * PlanningSignalRService
 *
 * Centralized service for handling planning-related SignalR events.
 * This service:
 * 
 * 1. Subscribes to planning-specific SignalR channels
 * 2. Processes planning lifecycle events (started, confirmed, failed)
 * 3. Coordinates with PlanningBoardFacade for state updates
 * 4. Handles multi-user awareness (shows when other users are planning)
 *
 * Future: Will handle optimistic UI confirmations/failures
 */

import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

import { PBoardChannelService } from './PBoardchannel.service';
import { DevLoggerService } from '../../shared/services/dev-logger.service';
import { ActionKeys } from '../../pb-screen/action.keys';
import { ChannelEvent } from '../../pb-screen/services/channel.event.factory';
import {
  PlanningStartedPayload,
  PlanningConfirmedPayload,
  PlanningFailedPayload
} from '../../shared/models/optimistic/optimistic-state.interface';

/**
 * Event types for planning operations
 */
export enum PlanningEventType {
  PLANNING_STARTED = 'PLANNING_STARTED',
  PLANNING_CONFIRMED = 'PLANNING_CONFIRMED',
  PLANNING_FAILED = 'PLANNING_FAILED',
  PLANNING_PROGRESS = 'PLANNING_PROGRESS'
}

/**
 * Generic planning event wrapper
 */
export interface PlanningEvent {
  type: PlanningEventType;
  correlationId: string;
  userId: string;
  timestamp: Date;
  payload: PlanningStartedPayload | PlanningConfirmedPayload | PlanningFailedPayload | any;
}

/**
 * Planning activity from another user (for multi-user awareness)
 */
export interface RemotePlanningActivity {
  userId: string;
  userName?: string;
  planningGroup: string;
  capacitySlotId: number;
  optimisticTOId: string;
  startedAt: Date;
}

@Injectable({
  providedIn: 'root'
})
export class PlanningSignalRService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private isInitialized = false;
  private currentUserId: string = '';

  // Subjects for emitting planning events to consumers
  private planningStarted$ = new Subject<PlanningStartedPayload>();
  private planningConfirmed$ = new Subject<PlanningConfirmedPayload>();
  private planningFailed$ = new Subject<PlanningFailedPayload>();

  // Track remote planning activities (for multi-user awareness)
  private remotePlanningActivities = new Map<string, RemotePlanningActivity>();
  private remotePlanningActivities$ = new Subject<Map<string, RemotePlanningActivity>>();

  constructor(
    private readonly channelService: PBoardChannelService,
    private readonly devLogger: DevLoggerService
  ) {}

  /**
   * Initialize the service and subscribe to planning channels
   * 
   * Call this after the user is authenticated and SignalR is connected
   */
  public initialize(userId: string, planningGroups: string[]): void {
    if (this.isInitialized) {
      this.devLogger.log('PlanningSignalR', 'Already initialized, skipping');
      return;
    }

    this.currentUserId = userId;
    this.devLogger.log('PlanningSignalR', `Initializing for user: ${userId}`, { planningGroups });

    // Subscribe to each planning group's channel
    for (const group of planningGroups) {
      this.subscribeToChannel(`planning-${group}`);
    }

    // Subscribe to user-specific channel for direct messages
    this.subscribeToChannel(`planning-user-${userId}`);

    this.isInitialized = true;
  }

  /**
   * Subscribe to a specific channel and handle events
   */
  private subscribeToChannel(channelName: string): void {
    this.devLogger.log('PlanningSignalR', `Subscribing to channel: ${channelName}`);

    this.channelService.sub(channelName).pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (event: ChannelEvent) => this.handleChannelEvent(channelName, event),
      (error) => this.devLogger.error('PlanningSignalR', `Channel error: ${channelName}`, error)
    );
  }

  /**
   * Handle incoming channel events
   */
  private handleChannelEvent(channel: string, event: ChannelEvent): void {
    // Only process planning-related events
    if (!this.isPlanningEvent(event)) {
      return;
    }

    this.devLogger.log('PlanningSignalR', `Received event on ${channel}`, { 
      type: event.name, 
      data: event.data 
    });

    const planningEvent = this.parsePlanningEvent(event);
    if (!planningEvent) return;

    switch (planningEvent.type) {
      case PlanningEventType.PLANNING_STARTED:
        this.handlePlanningStarted(planningEvent.payload as PlanningStartedPayload);
        break;

      case PlanningEventType.PLANNING_CONFIRMED:
        this.handlePlanningConfirmed(planningEvent.payload as PlanningConfirmedPayload);
        break;

      case PlanningEventType.PLANNING_FAILED:
        this.handlePlanningFailed(planningEvent.payload as PlanningFailedPayload);
        break;

      case PlanningEventType.PLANNING_PROGRESS:
        // Handle progress updates if needed
        break;
    }
  }

  /**
   * Check if an event is planning-related
   */
  private isPlanningEvent(event: ChannelEvent): boolean {
    return event.name?.startsWith('[Planning]') || 
           event.name?.startsWith('[Optimistic]') ||
           event.name === ActionKeys.PLANNING_STARTED ||
           event.name === ActionKeys.PLANNING_CONFIRMED ||
           event.name === ActionKeys.PLANNING_FAILED;
  }

  /**
   * Parse a channel event into a planning event
   */
  private parsePlanningEvent(event: ChannelEvent): PlanningEvent | null {
    try {
      let type: PlanningEventType;

      switch (event.name) {
        case ActionKeys.PLANNING_STARTED:
        case '[Planning] Started':
          type = PlanningEventType.PLANNING_STARTED;
          break;
        case ActionKeys.PLANNING_CONFIRMED:
        case '[Planning] Confirmed':
          type = PlanningEventType.PLANNING_CONFIRMED;
          break;
        case ActionKeys.PLANNING_FAILED:
        case '[Planning] Failed':
          type = PlanningEventType.PLANNING_FAILED;
          break;
        default:
          return null;
      }

      return {
        type,
        correlationId: event.data?.correlationId || '',
        userId: event.data?.userId || '',
        timestamp: new Date(),
        payload: event.data
      };
    } catch (e) {
      this.devLogger.error('PlanningSignalR', 'Failed to parse planning event', e);
      return null;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EVENT HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Handle PLANNING_STARTED event
   * 
   * This is received when any user (including self) starts a planning operation
   */
  private handlePlanningStarted(payload: PlanningStartedPayload): void {
    this.devLogger.log('PlanningSignalR', 'Planning started', {
      correlationId: payload.correlationId,
      initiatedBy: payload.initiatedByUserId,
      isSelf: payload.initiatedByUserId === this.currentUserId
    });

    // Track remote planning activity (from other users)
    if (payload.initiatedByUserId !== this.currentUserId) {
      this.trackRemoteActivity(payload);
    }

    // Emit to subscribers
    this.planningStarted$.next(payload);
  }

  /**
   * Handle PLANNING_CONFIRMED event
   * 
   * This is received when BC confirms a planning operation
   */
  private handlePlanningConfirmed(payload: PlanningConfirmedPayload): void {
    this.devLogger.log('PlanningSignalR', 'Planning confirmed', {
      correlationId: payload.correlationId,
      toId: payload.transportOrder?.id
    });

    // Remove from remote activities if present
    this.removeRemoteActivity(payload.correlationId);

    // Emit to subscribers
    this.planningConfirmed$.next(payload);
  }

  /**
   * Handle PLANNING_FAILED event
   * 
   * This is received when BC rejects a planning operation
   */
  private handlePlanningFailed(payload: PlanningFailedPayload): void {
    this.devLogger.log('PlanningSignalR', 'Planning failed', {
      correlationId: payload.correlationId,
      error: payload.errorMessage
    });

    // Remove from remote activities if present
    this.removeRemoteActivity(payload.correlationId);

    // Emit to subscribers
    this.planningFailed$.next(payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // REMOTE ACTIVITY TRACKING (Multi-user awareness)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Track a remote planning activity (from another user)
   */
  private trackRemoteActivity(payload: PlanningStartedPayload): void {
    const activity: RemotePlanningActivity = {
      userId: payload.initiatedByUserId,
      planningGroup: payload.planningGroup,
      capacitySlotId: payload.capacitySlotId,
      optimisticTOId: payload.optimisticTOId,
      startedAt: new Date()
    };

    this.remotePlanningActivities.set(payload.correlationId, activity);
    this.remotePlanningActivities$.next(new Map(this.remotePlanningActivities));

    this.devLogger.log('PlanningSignalR', 'Tracking remote activity', activity);
  }

  /**
   * Remove a remote activity (when confirmed or failed)
   */
  private removeRemoteActivity(correlationId: string): void {
    if (this.remotePlanningActivities.has(correlationId)) {
      this.remotePlanningActivities.delete(correlationId);
      this.remotePlanningActivities$.next(new Map(this.remotePlanningActivities));
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PUBLIC API - Observables for consumers
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Observable for planning started events
   */
  public get onPlanningStarted$(): Observable<PlanningStartedPayload> {
    return this.planningStarted$.asObservable();
  }

  /**
   * Observable for planning confirmed events
   */
  public get onPlanningConfirmed$(): Observable<PlanningConfirmedPayload> {
    return this.planningConfirmed$.asObservable();
  }

  /**
   * Observable for planning failed events
   */
  public get onPlanningFailed$(): Observable<PlanningFailedPayload> {
    return this.planningFailed$.asObservable();
  }

  /**
   * Observable for remote planning activities (multi-user awareness)
   */
  public get onRemoteActivitiesChanged$(): Observable<Map<string, RemotePlanningActivity>> {
    return this.remotePlanningActivities$.asObservable();
  }

  /**
   * Check if there's a conflict with a potential planning operation
   * 
   * @param capacitySlotId The capacity slot being planned to
   * @param timeFrom Start time of planning
   * @param timeUntil End time of planning
   * @returns Array of conflicting remote activities
   */
  public checkForConflicts(
    capacitySlotId: number,
    timeFrom?: Date,
    timeUntil?: Date
  ): RemotePlanningActivity[] {
    const conflicts: RemotePlanningActivity[] = [];

    this.remotePlanningActivities.forEach((activity) => {
      if (activity.capacitySlotId === capacitySlotId) {
        conflicts.push(activity);
      }
    });

    return conflicts;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PUBLISHING METHODS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Broadcast a planning started event to all users in the planning group
   */
  public broadcastPlanningStarted(payload: PlanningStartedPayload): void {
    const event: ChannelEvent = {
      name: ActionKeys.PLANNING_STARTED,
      data: payload,
      timestamp: new Date(),
      channelName: `planning-${payload.planningGroup}`,
      json: ''
    };

    this.channelService.publish(event);
    this.devLogger.log('PlanningSignalR', 'Broadcast planning started', payload);
  }

  /**
   * Broadcast a planning confirmed event
   */
  public broadcastPlanningConfirmed(payload: PlanningConfirmedPayload): void {
    const event: ChannelEvent = {
      name: ActionKeys.PLANNING_CONFIRMED,
      data: payload,
      timestamp: new Date(),
      channelName: '', // Will be set by server based on correlation
      json: ''
    };

    this.channelService.publish(event);
    this.devLogger.log('PlanningSignalR', 'Broadcast planning confirmed', { correlationId: payload.correlationId });
  }

  /**
   * Broadcast a planning failed event
   */
  public broadcastPlanningFailed(payload: PlanningFailedPayload): void {
    const event: ChannelEvent = {
      name: ActionKeys.PLANNING_FAILED,
      data: payload,
      timestamp: new Date(),
      channelName: '', // Will be set by server based on correlation
      json: ''
    };

    this.channelService.publish(event);
    this.devLogger.log('PlanningSignalR', 'Broadcast planning failed', { correlationId: payload.correlationId });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LIFECYCLE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Clean up subscriptions on destroy
   */
  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.planningStarted$.complete();
    this.planningConfirmed$.complete();
    this.planningFailed$.complete();
    this.remotePlanningActivities$.complete();
    this.remotePlanningActivities.clear();
  }
}
